type JSONPrimitive = string | number | boolean | null;
type JSONValue = JSONPrimitive | JSONObject | JSONArray;
interface JSONObject {
  [key: string]: JSONValue;
}
type JSONArray = JSONValue[];

function assertValidJSONValue(value: unknown): asserts value is JSONValue {
  if (
    value === null ||
    typeof value === 'string' ||
    typeof value === 'boolean'
  ) {
    return;
  }
  if (typeof value === 'number') {
    if (!Number.isFinite(value)) {
      throw new TypeError('Non-finite numbers are not permitted in canonical JSON');
    }
    return;
  }
  if (Array.isArray(value)) {
    for (const entry of value) {
      assertValidJSONValue(entry);
    }
    return;
  }
  if (typeof value === 'object') {
    const proto = Object.getPrototypeOf(value);
    if (proto !== Object.prototype && proto !== null) {
      throw new TypeError('Only simple objects are supported for canonical JSON');
    }
    for (const [key, entry] of Object.entries(value as Record<string, unknown>)) {
      if (typeof key !== 'string') {
        throw new TypeError('Object keys must be strings');
      }
      if (entry === undefined) {
        continue;
      }
      assertValidJSONValue(entry);
    }
    return;
  }
  throw new TypeError(`Unsupported JSON value type: ${typeof value}`);
}

function canonicalizeValue(value: JSONValue): string {
  if (value === null) {
    return 'null';
  }
  if (typeof value === 'boolean') {
    return value ? 'true' : 'false';
  }
  if (typeof value === 'number') {
    return JSON.stringify(Number(value));
  }
  if (typeof value === 'string') {
    return JSON.stringify(value);
  }
  if (Array.isArray(value)) {
    const entries = value.map((item) => canonicalizeValue(item));
    return `[${entries.join(',')}]`;
  }
  const keys = Object.keys(value).filter((key) => value[key] !== undefined).sort();
  const serialized = keys
    .map((key) => `${JSON.stringify(key)}:${canonicalizeValue(value[key] as JSONValue)}`)
    .join(',');
  return `{${serialized}}`;
}

export function canonicalize(value: unknown): string {
  assertValidJSONValue(value);
  return canonicalizeValue(value);
}

export function canonicalizeToBytes(value: unknown): Uint8Array {
  return new TextEncoder().encode(canonicalize(value));
}
